import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import ProgressNoteForm from "../ProgressNote/ProgressNoteForm";
import {
    fetchProgressNote,
    updateProgressNote,
} from "../../../store/slices/progressNotesSlice";

const EditProgressNote = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId, type_id } = state || {}; // ✅ Get type_id from navigation state

    const { isLoading } = useSelector(state => state.progressNotes);

    const [formData, setFormData] = useState(null);
    const [errors, setErrors] = useState({});

    // ✅ Fetch single progress note by ID
    useEffect(() => {
        const loadProgressNote = async () => {
            try {
                const response = await dispatch(fetchProgressNote(id)).unwrap();

                // ✅ Map backend (snake_case) → frontend (camelCase)
                const formatted = {
                    patientId: patientId || response.patient_id,
                    caseId: caseId || response.case_id,
                    type_id: response.type_id || type_id, // ✅ NEW: Get type_id
                    progressNotes:
                        response.details?.map((item) => ({
                            id: item.id,
                            note: item.progress_note || "",
                            date: item.note_date || "",
                        })) || [],
                };

                setFormData(formatted);
            } catch (error) {
                console.error("Error fetching progress note:", error);
                Swal.fire("Error", "Failed to load progress note.", "error");
            }
        };

        loadProgressNote();
    }, [dispatch, id, patientId, caseId, type_id]);

    // ✅ Handle input change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    // ✅ Submit updated data
    const handleSubmit = async (e) => {
        e.preventDefault();

        // ✅ Prepare payload (camelCase → snake_case)
        const payload = {
            patient_id: formData.patientId,
            case_id: formData.caseId,
            type_id: formData.type_id || null, // ✅ NEW: Include type_id
            progress_notes: formData.progressNotes.map((note) => ({
                note: note.note,
                date: note.date,
            })),
        };

        console.log("Update payload:", payload);

        try {
            await dispatch(updateProgressNote({
                case_id: caseId,
                noteData: payload
            })).unwrap();

            Swal.fire({
                title: "Updated!",
                text: "Progress Note updated successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            // Stay on edit page
        } catch (error) {
            console.error("Error updating progress note:", error);

            if (error.errors) {
                const flattened = Object.entries(error.errors).reduce(
                    (acc, [key, val]) => ({
                        ...acc,
                        [key]: Array.isArray(val) ? val.join(", ") : val,
                    }),
                    {}
                );
                setErrors(flattened);
            } else {
                Swal.fire("Error", "Something went wrong while updating.", "error");
            }
        }
    };

    if (!formData) {
        return (
            <MainLayout>
                <div className="container-xxl flex-grow-1 container-p-y">
                    <div className="text-center mt-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3">Loading progress note...</p>
                    </div>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/phase05/progress-notes/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>
                <div className="card">
                    <div className="card-body">
                        {/* Display type indicator */}
                        {formData.type_id && (
                            <div className="alert alert-info mb-3">
                                <i className="ri-information-line me-2"></i>
                                <strong>Note Type:</strong> {formData.type_id === 1 ? "Psychiatric" : formData.type_id === 4 ? "PRPS" : "Standard"}
                            </div>
                        )}

                        {/* Validation Errors */}
                        {Object.keys(errors).length > 0 && (
                            <div className="alert alert-danger">
                                {Object.entries(errors).map(([key, msg], i) => (
                                    <div key={i}>
                                        <strong>{key.replace(/\.\d+\./, " → ")}:</strong> {msg}
                                    </div>
                                ))}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <ProgressNoteForm
                                formData={formData}
                                setFormData={setFormData}
                                typeId={formData.type_id} // ✅ Pass type_id to form component
                            />
                            <div className="col-12 d-flex justify-content-end mt-4">
                                <div className="col-md-2 me-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary w-100"
                                        onClick={() => navigate(`/cases/${caseId}/profile`, {
                                            state: { patientId, caseId }
                                        })}
                                        disabled={isLoading}
                                    >
                                        <i className="ri-arrow-left-line me-1"></i> Cancel
                                    </button>
                                </div>
                                <div className="col-md-2">
                                    <button
                                        type="submit"
                                        className="btn btn-primary w-100 sticky-form-submit"
                                        disabled={isLoading}
                                    >
                                        <i className="ri-edit-2-line me-1"></i>
                                        {isLoading ? "Updating..." : "Update"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditProgressNote;